import { useEffect } from 'react';
import axios from 'axios';
import { getToken, removeToken } from './tokenService';
import { memoizedRefreshToken } from './refreshTokenService';
import { TOKEN, REFRESH_TOKEN } from '../common/constants/localStorage';
import { routesPath } from '../common/constants/routesPath';
import globalRouter from '../routes/GlobalRouter';
import globalNotification from '../common/hooks/globalNotification';
import { notificationType } from '../common/constants/notificationType';
import { constants } from '../common/constants/constants';
import getErrorTranslation, { SYSTEM_ERROR_CODES } from '../utils/axiosServiceHelper';
import { endImpersonation, isImpersonationEnded, isImpersonationTab } from './impersonationSession';
import { notifyReadOnly, notifyUnavailable } from './impersonationNotices';
import {
    IMPERSONATION_API,
    IMPERSONATION_END_REASONS,
    IMPERSONATION_ERROR_CODES,
} from '../common/constants/impersonation';

const lang = localStorage.getItem('i18nextLng');

function authInterceptor(config) {
    if (isImpersonationTab()) {
        const refusal = refuseImpersonatedRequest(config);

        if (refusal) {
            return Promise.reject(refusal);
        }
    }

    const finalConfig = { ...config };
    // in an impersonation tab tokenService reads the impersonation token from sessionStorage, never localStorage
    const token = getToken(TOKEN);

    if (!finalConfig.headers) {
        finalConfig.headers = {};
    }
    if (token) {
        finalConfig.headers.Authorization = `Bearer ${token}`;
    }

    return finalConfig;
}
// Creating axios instance for all fetch requests
const instance = axios.create({
    baseURL: `${constants.API_BASE_URL}`,
});

// Creating axios instance for refreshing token request only
const refreshTokenInstance = axios.create({
    baseURL: `${constants.API_BASE_URL}`,
});

instance.interceptors.request.use(authInterceptor);

instance.interceptors.response.use(
    res => {
        return res;
    },
    async err => {
        // an impersonation tab never refreshes; its own codes are handled first, other statuses as usual below
        if (isImpersonationTab()) {
            const handled = handleImpersonationError(err);

            if (handled) {
                return handled;
            }
        }

        const originalConfig = err.config;
        const errStatus = err?.response?.status;
        const errStatusText = err?.response?.statusText;

        // Access Token was expired
        if (errStatus !== 401) {
            // a background request that would only repeat an error the caller has already shown
            if (originalConfig?.quietErrors) {
                return Promise.reject(err);
            }
            switch (errStatus) {
                case 400:
                case 423: // TOTP lockout (TOTP_LOCKED), shown by the 2FA form that sent the code
                    // We will display a notification at the same level when we send a request to the server
                    // Here, we simply reject the promise to avoid double notifications on bad request errors
                    return Promise.reject(err);
                case 403:
                    navigateWithError(errStatus, getErrorTranslation(403, lang, 'description'));
                    break;
                case 404:
                    navigateWithError(errStatus, getErrorTranslation(404, lang, 'description'));
                    break;
                case 429:
                    globalNotification.open({
                        type: notificationType.ERROR,
                        message: getErrorTranslation(429, lang, 'message'),
                        description: getErrorTranslation(429, lang, 'description'),
                        placement: 'top',
                    });
                    break;
                default:
                    globalNotification.open({
                        type: notificationType.ERROR,
                        message: errStatus
                            ? errStatus + ' ' + errStatusText
                            : getErrorTranslation(500, lang, 'message'),
                        description: getErrorTranslation(500, lang, 'description'),
                        placement: 'top',
                    });
                    return Promise.reject(err);
            }

            return Promise.reject(err);
        }

        // A replayed request that 401s again is not refreshed again: the server rejects this token
        // (e.g. a session from before a token-format change), so retrying would loop forever.
        if (originalConfig._retry) {
            return Promise.reject(err);
        }
        originalConfig._retry = true;

        // Refreshing token only once
        const result = await memoizedRefreshToken();

        if (result) {
            originalConfig.headers = {
                ...originalConfig.headers,
                Authorization: `Bearer ${result.accessToken}`,
            };
            return instance(originalConfig);
        } else {
            return Promise.reject(err);
        }
    }
);

// Catch error during token refreshing process.
refreshTokenInstance.interceptors.response.use(
    res => {
        return res;
    },
    async err => {
        // an impersonation tab has no refresh token, and must never sign anyone out or land on the sign-in page
        if (isImpersonationTab()) {
            endImpersonation(IMPERSONATION_END_REASONS.ENDED);
            return Promise.reject(err);
        }

        refreshTokenInstance.post('/auth-consumers/logout', { refreshToken: getToken(REFRESH_TOKEN) });
        removeToken(TOKEN);
        removeToken(REFRESH_TOKEN);

        const { AUTH_EXPIRED } = SYSTEM_ERROR_CODES;

        await globalNotification.open({
            type: notificationType.ERROR,
            message: getErrorTranslation(AUTH_EXPIRED, lang, 'message'),
            description: getErrorTranslation(AUTH_EXPIRED, lang, 'description'),
            placement: 'top',
        });

        setTimeout(() => window.location.replace('/auth/sign-in'), 3000);
    }
);

// #region Read-only impersonation (#790)
// The API refuses every write of an impersonation token and is the real control; the client only avoids sending
// what would be refused, never refreshes, and leaves the tab once the session is over.

// Why an impersonation tab may not send this request, or null when it may.
function refuseImpersonatedRequest(config) {
    // the tab is on its way to the dealer app: nothing else goes out
    if (isImpersonationEnded()) {
        return new axios.CanceledError('The impersonation has ended.', null, config);
    }

    const method = (config.method || 'get').toLowerCase();

    if (method !== 'get' && !(method === 'post' && requestPath(config) === IMPERSONATION_API.STOP)) {
        // shaped like the server's refusal, so the response interceptor shows its one read-only toast
        return new axios.AxiosError(
            'Not available in read-only impersonation.',
            'ERR_IMPERSONATION_READ_ONLY',
            config,
            null,
            {
                status: 403,
                statusText: 'Forbidden',
                headers: {},
                config,
                data: { code: IMPERSONATION_ERROR_CODES.READ_ONLY },
            }
        );
    }

    return null;
}

function requestPath(config) {
    const url = config.url || '';
    const baseURL = config.baseURL || '';
    const path = baseURL && url.startsWith(baseURL) ? url.slice(baseURL.length) : url;

    return path.split('?')[0];
}

// A rejection for the impersonation outcomes, or null to fall through to the usual status handling.
function handleImpersonationError(err) {
    // refused because the tab is leaving: nothing to show
    if (axios.isCancel(err)) {
        return Promise.reject(err);
    }

    const errStatus = err?.response?.status;
    const errCode = err?.response?.data?.code;

    // no refresh token exists here: any 401 means the session is over (stopped, expired or revoked)
    if (errStatus === 401) {
        endImpersonation(IMPERSONATION_END_REASONS.ENDED);
        return Promise.reject(err);
    }

    // a write the session may not make: say so and stay on the page instead of opening the error page
    if (errStatus === 403 && errCode === IMPERSONATION_ERROR_CODES.READ_ONLY) {
        notifyReadOnly();
        return Promise.reject(err);
    }

    // the server could not check the session: transient, so the session is kept and the user may retry
    if (errStatus === 503 && errCode === IMPERSONATION_ERROR_CODES.UNAVAILABLE) {
        notifyUnavailable();
        return Promise.reject(err);
    }

    return null;
}
// #endregion

function navigateWithError(status, subTitle) {
    globalRouter.navigate(routesPath.ERROR.PATH, {
        state: {
            status,
            title: status,
            subTitle,
        },
    });
}

export const axiosInstance = instance;

export const axiosRefTokenInst = refreshTokenInstance;

export const CancelSignal = axios.CancelToken.source;

export const isCancelSignal = e => axios.isCancel(e);

export const useAxios = () => {
    const signal = CancelSignal();

    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            signal.cancel('Unmount useAxios');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { axios: axiosInstance, cancelToken: signal.token };
};
